/* eslint react-hooks/exhaustive-deps: "warn" */

import NextLink from 'next/link';

import { Faq as FaqType } from 'libs/http/api/content/content.types';

import { useInt } from 'hooks/useInt';

import { Faq } from 'ui/atoms/Faq/Faq';
import { Section } from 'ui/molecules/Section/Section';

import styles from './GovernmentFaq.module.scss';

type Translate = (key: string) => string;

const getItems = (t: Translate): FaqType[] => [
  {
    id: 'national-registry',
    default_opened: true,
    question: t('We already have our own national registry. What are we buying?'),
    answer: t(
      'Everything outside it. Your registry is authoritative for entities incorporated in your jurisdiction and silent on everything else, which is exactly where the risk sits, because a bidder incorporates domestically and is controlled from abroad. We hold <b>the other 400+ registries</b>, resolve entities across them, and compute the ownership chain end to end.',
    ),
  },
  {
    id: 'source',
    question: t('Where does the data actually come from?'),
    answer: t(
      "Directly from official government registries and statutory filings. We do not resell a third party's compilation and we do not blend in unattributed sources. Every record carries <b>its registry, its filing reference and its retrieval date</b>. Anything we compute rather than source is labelled as modelled wherever it appears.",
    ),
  },
  {
    id: 'coverage',
    question: t('Which jurisdictions do you cover, and how do we check before committing?'),
    answer: t(
      '400+ government registers across 200+ countries. Coverage is never uniform, so we send <b>a jurisdiction-by-jurisdiction, field-by-field matrix</b> before contract — including where a register publishes beneficial ownership, where it publishes only directors and officers, and where it publishes neither. If a country you depend on is thin, you will see that in the matrix rather than after signature.',
    ),
  },
  {
    id: 'current',
    question: t('How current is it?'),
    answer: t(
      'Registry data is synced daily. We do not describe registry data as real-time, because registries themselves are not real-time — a filing appears when it is filed. What we commit to is <b>a stated sync cadence per source</b>, and change events when a record moves.',
    ),
  },
  {
    id: 'monitoring',
    question: t('Do you keep checking after we have screened an entity?'),
    answer: t(
      'Yes. Every entity in the file is re-checked daily, along with every node above it in the ownership graph, across <b>38 change types</b> covering ownership, officers, standing, identity, financial filings and screening. A public contract runs for years; a check at award is a snapshot, and the changes that matter usually arrive afterwards.',
    ),
  },
  {
    id: 'appeal',
    question: t('Can we use it in decisions that may be appealed or reviewed?'),
    answer: t(
      'That is what the provenance model is for. Every value traces back to <b>the source document and the date it was read</b>, which is the difference between a finding you can defend and a score you cannot explain. We supply the evidence; the determination remains yours.',
    ),
  },
  {
    id: 'dpa',
    question: t('What about data protection and residency?'),
    answer: t(
      'The core dataset is company information from public registers. Where records contain personal data such as directors and beneficial owners, handling is governed by the contract, and <b>residency and processing constraints are scoped with your information governance team</b> before deployment rather than assumed.',
    ),
  },
  {
    id: 'environment',
    question: t('Can we run it inside our own environment?'),
    answer: t(
      'Where policy prevents outbound calls, the dataset can be delivered for processing <b>entirely within your own estate</b> rather than queried across a network boundary. Deployment shape, residency and processing location are scoped with your security team before anything is agreed, not assumed afterwards.',
    ),
  },
  {
    id: 'exit',
    question: t('What happens to the data if we stop the contract?'),
    answer: t(
      'Records key on the registration number held by the source register, plus <b>the Legal Entity Identifier where one exists</b>. We publish no proprietary company key, so identifiers in data you already hold keep resolving at the register after the contract ends. Exit provisions — the format of the final extract and the notice period — are written into the agreement rather than negotiated at the point you want to leave.',
    ),
  },
  {
    id: 'buy',
    question: t('How do we buy it?'),
    answer: t(
      'Direct contract, priced on <b>the jurisdictions, fields and volumes you actually need</b> rather than a bundled enterprise tier. We scope a pilot on a live use case — a real tender, a real screening backlog — before anyone signs anything longer.',
    ),
  },
];

export const GovernmentFaq = () => {
  const { t } = useInt();

  return (
    <Section className={styles.root}>
      <p className={styles.lede}>{t('If yours is not here, put it to the team directly.')}</p>

      <NextLink className={styles.contact} href="/contact-us">
        <span className={styles.contactIcon}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M4 5.5h16v13H4z" />
            <path d="M4.5 6l7.5 6.5L19.5 6" />
          </svg>
        </span>
        <span className={styles.contactLabel}>{t('Contact the team')}</span>
        <span className={styles.contactArrow} aria-hidden="true">
          →
        </span>
      </NextLink>

      <Faq title={t('The questions that decide a public-sector purchase.')} data={getItems(t)} />
    </Section>
  );
};
